import { inject, Injectable } from '@angular/core';

import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, take, switchMap, timeout, catchError } from 'rxjs/operators';
import { AuthTokenService } from '../services';
import { PendingEmailConfirmationService } from '@features/auth-amplify/services/pending-email-confirmation.service';

import { UserActions } from '@features/auth/state/actions';
import { getIsUserVerified } from '@features/auth/state/selectors';
import { IAppState } from '@shared/models';
import { CanActivateFn } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class LoggedInUserGuard {
  constructor(
    private readonly store$: Store<IAppState>,
    private readonly authTokenService: AuthTokenService,
    private readonly pendingSvc: PendingEmailConfirmationService
  ) {}

  public canActivate(): Observable<boolean> {
    // Strategy: attempt quick token presence retrieval first; if user slice not yet populated, wait a short window
    // for token (idToken) resolution before deciding invalid.
    return this.store$.pipe(select(getIsUserVerified)).pipe(
      switchMap((verified) => {
        if (verified) return of(true);
        if (this.pendingSvc.isPending()) {
          this.store$.dispatch(UserActions.invalidTokenDetected());
          return of(false);
        }
        return this.authTokenService.getIdToken().pipe(
          timeout(2000),
          switchMap((tok) => {
            if (tok) {
              // Allow navigation; downstream effects will populate user state.
              return of(true);
            }
            return this.store$.pipe(
              select(getIsUserVerified),
              take(1),
              map((v) => {
                if (!v) {
                  this.store$.dispatch(UserActions.invalidTokenDetected());
                  return false;
                }
                return true;
              })
            );
          }),
          catchError(() => {
            this.store$.dispatch(UserActions.invalidTokenDetected());
            return of(false);
          })
        );
      }),
      take(1)
    );
  }
}

export const canActivateLoggedInUser: CanActivateFn = () => inject(LoggedInUserGuard).canActivate();
